import React, { useState, useEffect, useContext } from 'react';
import {
  Grid, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem
} from '@mui/material';

import { ConstantsContext, ThemeContext } from "../index";
import DashboardTopMenu from './DashboardTopMenu';

export default function RouteAdminUserManagement(props) {

  const [theme] = useContext(ThemeContext);
  const [usersArr, setUsersArr] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per_page: 10, total: 0, total_pages: 0 });
  const [errorMessage, setErrorMessage] = useState('');
  const [filters, setFilters] = useState({
    usertype: '100',
    is_admin: '100',
    email: '',
    is_blocked: '100',
    is_deleted: '100',
    paid: '100',
    is_ai_agent: '100'
  });

  const [editUser, setEditUser] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [staticText] = useContext(ConstantsContext);

  useEffect(() => {
    let isMounted = true;
    _api_get_all_users(pagination.page, isMounted);
    return () => { isMounted = false; };
  }, []);

  const _api_get_all_users = (pageNumber = 1, isMounted = true) => {
    const data = [
      { key: 'action', value: 'get_all_users' },
      { key: 'loggined_token', value: props.userDetails.loggined_token },
      { key: 'page', value: pageNumber },
      { key: 'per_page', value: pagination.per_page },
      { key: 'usertype', value: filters.usertype },
      { key: 'is_admin', value: filters.is_admin },
      { key: 'email', value: filters.email },
      { key: 'is_blocked', value: filters.is_blocked },
      { key: 'is_deleted', value: filters.is_deleted },
      { key: 'paid', value: filters.paid },
      { key: 'is_ai_agent', value: filters.is_ai_agent }
    ];

    props._apiConnect(staticText.path_user, data, (res) => {
      if (!isMounted) return;
      if (res.status_code === 2) {
        setUsersArr(res.data);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      } else {
        setErrorMessage(res.msg || 'Unknown error occurred');
      }
    });
  };

  const updateFilter = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    _api_get_all_users(1, true);
  };

  const handlePrev = () => {
    if (pagination.page > 1) goToPage(pagination.page - 1);
  };

  const handleNext = () => {
    if (pagination.page < pagination.total_pages) goToPage(pagination.page + 1);
  };

  const goToPage = (pageNumber) => {
    const page = Math.max(1, pageNumber); // 👈 ensure minimum page is 1
    setPagination(prev => ({ ...prev, page }));
    _api_get_all_users(page, true);
  };

  const getVisiblePages = (current, total) => {
    const maxVisible = 10;
    let start = Math.max(1, current - 4);
    let end = Math.min(start + maxVisible - 1, total);
    if (end - start < maxVisible - 1) {
      start = Math.max(1, end - maxVisible + 1);
    }
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };

  const openEditModal = (user) => {
    setEditUser({ ...user });
    setOpenEditDialog(true);
  };

  const closeEditModal = () => {
    setOpenEditDialog(false);
    setEditUser(null);
  };

  const handleEditChange = (field, value) => {
    setEditUser(prev => ({ ...prev, [field]: value }));
  };

  const saveUserEdits = () => {
    console.log("Updated user data:", editUser);
    closeEditModal();
  };

  return (
    <Grid container spacing={2} style={{ width: '100%', margin: 0 }}>
      <DashboardTopMenu _apiLogOff={props._apiLogOff} userDetails={props.userDetails} />

      {/* Filters */}
      <Grid item xs={12}>
        <Typography variant="h6">Filters</Typography>
        <Grid container spacing={2} sx={{ marginBottom: 2 }}>
          {['usertype', 'is_admin', 'is_blocked', 'is_deleted', 'paid', 'is_ai_agent'].map(key => (
            <Grid item xs={2} key={key}>
              <TextField
                select
                fullWidth
                label={key}
                value={filters[key]}
                onChange={(e) => updateFilter(key, String(e.target.value))}
              >
                <MenuItem value="100">All</MenuItem>
                {key === 'usertype'
                  ? [
                      <MenuItem key="0" value="0">Regular User</MenuItem>,
                      <MenuItem key="1" value="1">Contributor</MenuItem>
                    ]
                  : [
                      <MenuItem key="0" value="0">No</MenuItem>,
                      <MenuItem key="1" value="1">Yes</MenuItem>
                    ]
                }
              </TextField>
            </Grid>
          ))}
          <Grid item xs={3}>
            <TextField
              fullWidth
              label="Email"
              value={filters.email}
              onChange={(e) => updateFilter('email', e.target.value)}
            />
          </Grid>
          <Grid item xs={1}>
            <Button variant="contained" fullWidth onClick={applyFilters}>Search</Button>
          </Grid>
        </Grid>
      </Grid>

      {/* Table */}
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>User Management</Typography>
        <Typography variant="subtitle2" style={{ marginTop: 10 }}>Total Users: {pagination.total}</Typography>

        {errorMessage && <div style={{ color: 'red' }}>{errorMessage}</div>}

        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 20 }}>
          <thead style={{ backgroundColor: '#F5F5F5' }}>
            <tr>
              <th style={thStyle}>ID</th>
              <th style={thStyle}>Username</th>
              <th style={thStyle}>Email</th>
              <th style={thStyle}>Usertype</th>
              <th style={thStyle}>Blocked</th>
              <th style={thStyle}>Deleted</th>
              <th style={thStyle}>AI Agent</th>
              <th style={thStyle}>Created</th>
              <th style={thStyle}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {usersArr.map((user, index) => (
              <tr key={index}>
                <td style={tdStyle}>{user.id}</td>
                <td style={tdStyle}>{user.username}</td>
                <td style={tdStyle}>{user.email}</td>
                <td style={tdStyle}>
                  {user.usertype === "0"
                    ? "Regular User"
                    : user.usertype === "1"
                    ? "Contributor"
                    : user.is_admin === "1"
                    ? "Admin"
                    : "Unknown"}
                </td>
                <td style={tdStyle}>{user.is_blocked === "1" ? "Yes" : "No"}</td>
                <td style={tdStyle}>{user.is_deleted === "1" ? "Yes" : "No"}</td>
                <td style={tdStyle}>{user.is_ai_agent === "1" ? "Yes" : "No"}</td>
                <td style={tdStyle}>{user.creation_date}</td>
                <td style={tdStyle}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => {
                      const { creation_date, ...cleanUser } = user;
                      openEditModal(cleanUser);
                    }}
                  >
                    Edit
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <Button variant="outlined" onClick={() => goToPage(1)} disabled={pagination.page === 1}>First</Button>
          <Button variant="outlined" onClick={handlePrev} disabled={pagination.page === 1}>Prev</Button>
          {getVisiblePages(pagination.page, pagination.total_pages).map((page) => (
            <Button
              key={page}
              variant={page === pagination.page ? "contained" : "outlined"}
              onClick={() => goToPage(page)}
            >
              {page}
            </Button>
          ))}
          {pagination.total_pages > 10 && pagination.page < pagination.total_pages - 5 && (
            <>
              <span style={{ margin: '0 8px' }}>...</span>
              <Button variant="outlined" onClick={() => goToPage(pagination.total_pages)}>
                {pagination.total_pages}
              </Button>
            </>
          )}
          <Button variant="outlined" onClick={handleNext} disabled={pagination.page === pagination.total_pages}>Next</Button>
          <Button variant="outlined" onClick={() => goToPage(pagination.total_pages)} disabled={pagination.page === pagination.total_pages}>Last</Button>
        </div>
      </Grid>

      {/* Modal */}
      <Dialog open={openEditDialog} onClose={closeEditModal} maxWidth="md" fullWidth>
        <DialogTitle>Edit User</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {[
              'username', 'email',
              'telegram', 'telegram_numeric', 'youtube', 'twitter',
              'api_key', 'api_key_public'
            ].map(field => (
              <Grid item xs={6} key={field}>
                <TextField
                  label={field.replace(/_/g, ' ')}
                  fullWidth
                  value={editUser?.[field] || ''}
                  onChange={(e) => handleEditChange(field, e.target.value)}
                />
              </Grid>
            ))}
            {['usertype', 'is_blocked', 'is_deleted', 'paid', 'is_ai_agent'].map(field => (
              <Grid item xs={6} key={field}>
                <TextField
                  select
                  label={field.replace(/_/g, ' ')}
                  fullWidth
                  value={editUser?.[field] || '0'}
                  onChange={(e) => handleEditChange(field, e.target.value)}
                >
                  <MenuItem value="0">No</MenuItem>
                  <MenuItem value="1">Yes</MenuItem>
                  {field === 'usertype' && <MenuItem value="2">Admin</MenuItem>}
                </TextField>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditModal} color="secondary">Cancel</Button>
          <Button onClick={saveUserEdits} color="primary" variant="contained">Save</Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}

const thStyle = {
  border: '1px solid #ccc',
  padding: '8px',
  backgroundColor: '#F5F5F5',
  color: 'black',
  textAlign: 'left'
};

const tdStyle = {
  border: '1px solid #ccc',
  padding: '8px',
  backgroundColor: '#F9F9F9',
  color: 'black'
};
